/**
 * Password Strength Checker Utility
 * Validates password strength and provides feedback
 */

/**
 * Check password strength and return score (0-4) and feedback
 * @param {string} password - The password to check
 * @returns {Object} - { score: number (0-4), strength: string, feedback: string[], isValid: boolean }
 */
export function checkPasswordStrength(password) {
  if (!password) {
    return {
      score: 0,
      strength: "none",
      feedback: [],
      isValid: false,
    };
  }

  const feedback = [];
  let score = 0;

  // Length check (minimum 8 characters)
  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push("At least 8 characters");
  }

  // Uppercase letter check
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("One uppercase letter");
  }

  // Lowercase letter check
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("One lowercase letter");
  }

  // Number check
  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push("One number");
  }

  // Special character check
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    score += 1;
  } else {
    feedback.push("One special character (!@#$%^&*)");
  }

  // Determine strength level
  let strength;
  if (score <= 1) {
    strength = "weak";
  } else if (score === 2) {
    strength = "fair";
  } else if (score === 3) {
    strength = "good";
  } else if (score === 4) {
    strength = "strong";
  } else {
    strength = "very-strong";
  }

  // Password is valid if it meets all requirements (score === 5)
  const isValid = score === 5;

  return {
    score,
    strength,
    feedback,
    isValid,
  };
}

/**
 * Get color for password strength indicator
 * @param {string} strength - The strength level
 * @returns {string} - Tailwind CSS color class
 */
export function getStrengthColor(strength) {
  switch (strength) {
    case "weak":
      return "bg-red-500";
    case "fair":
      return "bg-orange-500";
    case "good":
      return "bg-yellow-500";
    case "strong":
      return "bg-green-500";
    case "very-strong":
      return "bg-green-600";
    default:
      return "bg-gray-300";
  }
}

/**
 * Get text label for password strength
 * @param {string} strength - The strength level
 * @returns {string} - Human-readable label
 */
export function getStrengthLabel(strength) {
  switch (strength) {
    case "weak":
      return "Weak";
    case "fair":
      return "Fair";
    case "good":
      return "Good";
    case "strong":
      return "Strong";
    case "very-strong":
      return "Very Strong";
    default:
      return "";
  }
}

